import FormCompanyImage from 'components/form/FormCompanyImage';
import FormText from 'components/form/FormText';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';

import { useState, type JSX } from 'react';
import type { UseFieldArrayReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useAtom } from 'jotai';
import { ChatState } from 'stores/atoms/chatState';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import WsFormEditQuestionOptions from 'pages/ws/formEdit/components/formEditQuestions/formEditQuestionOptions/WsFormEditQuestionOptions';
import { isEmpty } from 'utils/app';

/**
 * フォーム作成編集 設問設定コンポーネント
 */
export default function WsFormEditQuestions(props: {
  readOnly: boolean;
  fieldArray: UseFieldArrayReturn<FormEditFormStateType, 'questions', 'id'>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  const [chatState] = useAtom(ChatState);
  const [processing] = useState(false);

  const {
    control,
    register,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<FormEditFormStateType>();

  const { append, move, remove, fields } = props.fieldArray;

  // 登録可能最大件数
  const limit = 25;

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */

  /**
   * 設問の順番を入れ替えます。
   */
  const onMove = (questionIndex: number, upper: boolean) => {
    // 先頭の場合は上に移動不可
    if (upper && questionIndex === 0) {
      return;
    }
    // 最終行の場合は下に移動不可
    if (!upper && questionIndex === fields.length - 1) {
      return;
    }
    // 要素の入れ替え
    move(questionIndex, upper ? questionIndex - 1 : questionIndex + 1);
  };

  /**
   * 設問を削除します。
   */
  const onRemove = (questionIndex: number) => {
    // 1件以下の場合は削除不可
    if (fields.length <= 1) {
      return;
    }
    // 要素の削除
    remove(questionIndex);
  };

  /**
   * 設問を追加します。
   */
  const onAppend = () => {
    // 上限値の以上の場合は追加不可
    if (fields.length >= limit) {
      return;
    }
    // 要素の追加
    append({
      questionType: DivConst.items.questionType.text.value.toString(),
      requiredFlag: DivConst.items.questionDefault.defaultRequiredFlag,
      textMaxLength: DivConst.items.questionDefault.textMaxLength,
      numberMaxLength: DivConst.items.questionDefault.numberMaxLength,
      checkboxMinCount: DivConst.items.questionDefault.checkboxMinCount,
      checkboxMaxCount: DivConst.items.questionDefault.checkboxMaxCount,
      options: [{}, {}, {}],
    });
  };

  /**
   * AIによる設問作成をチャットスライダーで行います。
   */
  const onCreateOptions = (questionIndex: number) => {
    const questions = getValues().questions;
    let isValid = true;

    clearErrors();

    // バリデーション
    if (isEmpty(questions[questionIndex].title)) {
      setError(`questions.${questionIndex}.title`, {
        type: 'required',
      });
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    // チャット外部送信関数を使用
    const newMessage = `設問${questionIndex + 1}の選択肢を作り直して`;

    if (chatState.setMessage) {
      // メッセージを送信（チャットサイドバー側で自動的に開く）
      chatState.setMessage(newMessage, false);
    }
  };

  /**
   * フォームの必須検証をします。
   */
  // const _validRequired = (values: FormEditFormStateType, name: string): boolean => {
  //   const value = get(values, name);

  //   if (isEmpty(value)) {
  //     setError(name as any, {
  //       type: 'custom',
  //       message: '必須項目です。',
  //     });
  //     return false;
  //   }
  //   return true;
  // };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング */}
      <IndicatorScreen visible={processing} />

      <section className="section-form-default section-question">
        <div className="container">
          {fields.map((field, questionIndex) => {
            return (
              <div key={field.id} className="box-common mb-3">
                <h3 className="title-section">設問{questionIndex + 1}</h3>

                <div className="form-default-container">
                  <div className="row row-0">
                    <div className="col-md col-header">
                      <div className="card form-header">
                        <h4 className="title">設問用の画像</h4>
                        <FormCompanyImage
                          control={control}
                          name={`questions.${questionIndex}.questionImageKey`}
                          readOnly={props.readOnly}
                        />
                      </div>
                    </div>
                    <div className="col-md col-form ps-md-5 ms-md-2">
                      <div className="btn-group-header text-end">
                        <div className="btn-group">
                          <button
                            className="btn"
                            onClick={() => onMove(questionIndex, true)}
                            disabled={props.readOnly || questionIndex === 0}
                          >
                            <img src="/img/icon/arrow-up-black.svg" alt="" />
                          </button>
                          <button
                            className="btn"
                            onClick={() => onMove(questionIndex, false)}
                            disabled={props.readOnly || questionIndex === fields.length - 1}
                          >
                            <img src="/img/icon/arrow-up-black.svg" alt="" className="scaley-1" />
                          </button>
                          <button
                            className="btn"
                            onClick={() => onRemove(questionIndex)}
                            disabled={props.readOnly || fields.length <= 1}
                          >
                            <img src="/img/icon/trash.svg" className="filter-black" />
                          </button>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="privacy-policy" className="form-label">
                          設問
                        </label>
                        <div className="row align-items-center">
                          <div className="col-md">
                            <FormText
                              name={`questions.${questionIndex}.title`}
                              placeholder="設問を入れよう"
                              register={register}
                              errors={errors}
                              readOnly={props.readOnly}
                              maxLength={100}
                            />
                          </div>
                          <div className="col-md col-btn flex-grow-0">
                            <div className="btn-container max-width-130 mt-md-0">
                              {/* 選択肢が必要な入力タイプの場合のみ表示 */}
                              {(() => {
                                const questionType = watch(
                                  `questions.${questionIndex}.questionType`
                                );
                                const showGenerateButton = [
                                  DivConst.items.questionType.radio.value.toString(),
                                  DivConst.items.questionType.checkbox.value.toString(),
                                  DivConst.items.questionType.select.value.toString(),
                                ].includes(questionType);

                                return showGenerateButton ? (
                                  <button
                                    onClick={() => onCreateOptions(questionIndex)}
                                    disabled={props.readOnly}
                                    className="btn btn-common ai-generate w-100"
                                  >
                                    選択肢の生成
                                  </button>
                                ) : null;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* オプション */}
                      <WsFormEditQuestionOptions
                        questionIndex={questionIndex}
                        readOnly={props.readOnly}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="btn-container max-width-238 mx-auto">
            <button
              onClick={onAppend}
              disabled={fields.length >= limit || props.readOnly}
              className="btn btn-common moderate-blue w-100"
            >
              + 設問を追加
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
